/* eslint-disable id-length */
import { TooltipModel } from "chart.js";
import { Fragment, useEffect } from "react";
import { reverse, take } from "lodash";

import {
  Card,
  CardSection,
  CardChart,
  CardLabel,
  CardLabelWithTooltip,
} from "~/components/ClustersInsights/Card";
import LineChart from "~/components/ClustersInsights/LineChart";
import { useChart } from "~/components/shared/Chart";
import { TimePeriod } from "~/components/ClustersInsights/Renderer";
import { Tooltip, TooltipLabel, TooltipValue } from "~/components/ClustersInsights/Tooltip";
import { ChartLegend } from "~/components/ClustersInsights/ChartLegend";
import { useChartData } from "~/components/ClustersInsights/Chart/hooks";
import {
  calculateMin,
  calculateMax,
  getLatestValue,
  getTimePeriodString,
  calculateSum,
  Scope,
} from "~/components/ClustersInsights/Chart/utils";
import { stackedChart, chartOptionsForScope } from "~/components/ClustersInsights/chartOptions";

interface DataPoint {
  x: number;
  active: number;
  percentage: number;
  running_jobs: number;
}

interface Props {
  scope: Scope;
  dataset: {
    label: string;
    data: DataPoint[];
  }[];
  timePeriod: TimePeriod;
}

export function RunningJobs({ scope, dataset, timePeriod }: Props) {
  const { chartRef, updateData } = useChart<"line", DataPoint>();

  const data = useChartData({
    datasets: dataset,
    orderDatasetBy: ({ data }) => calculateMax(data, "running_jobs"),
    orderDirection: "asc",
    colorsOffset: 3,
  });

  useEffect(() => {
    updateData(data);
  }, [data, updateData]);

  return (
    <Card className="lg:flex-row">
      <div className="flex min-w-0 grow flex-col gap-3">
        <CardSection>
          <CardLabelWithTooltip
            label="Running Jobs"
            description={`Number of running jobs across clusters · ${getTimePeriodString(timePeriod)}`}
            tooltip={{
              title: "Running Jobs",
              description:
                "Shows running jobs by cluster. This profile should align closely with agent utilization. Deviations away from the agent utilization profile may indicate blocked or stalled agents.",
            }}
          />
        </CardSection>

        <CardChart>
          <LineChart
            ref={chartRef}
            data={data}
            tooltip={ChartTooltip}
            options={{
              ...chartOptionsForScope(scope, {
                [Scope.Organization]: stackedChart,
                [Scope.Cluster]: stackedChart,
              }),
              parsing: { yAxisKey: "running_jobs", xAxisKey: "x" },
            }}
          />
        </CardChart>
      </div>
      <ChartLegend
        reverse={true}
        datasets={data.datasets}
        chartRef={chartRef}
        columns={{
          Min: ({ data }) => calculateMin(data, "running_jobs"),
          Max: ({ data }) => calculateMax(data, "running_jobs"),
          "Running Jobs": ({ data }) => getLatestValue(data, "running_jobs"),
        }}
      />
    </Card>
  );
}

const ChartTooltip = ({ tooltip }: { tooltip: TooltipModel<"line"> }) => {
  const totalRunning = calculateSum(tooltip.dataPoints.map(({ parsed }) => parsed));

  const visibleDataPoints = take(reverse(tooltip.dataPoints), 8);
  const hiddenDataPoints = tooltip.dataPoints.length - 8;

  return (
    <Tooltip tooltip={tooltip}>
      {visibleDataPoints.map(({ dataset, parsed }) => (
        <Fragment key={dataset.label}>
          <TooltipLabel color={dataset.borderColor as string}>{dataset.label}</TooltipLabel>
          <TooltipValue>{parsed.y}</TooltipValue>
        </Fragment>
      ))}

      <TooltipLabel>Total</TooltipLabel>
      <TooltipValue>{totalRunning}</TooltipValue>

      {hiddenDataPoints > 0 && (
        <span className="text-charcoal-300 col-span-2 text-[11px] font-medium">
          ({hiddenDataPoints} not shown)
        </span>
      )}
    </Tooltip>
  );
};
